// @ts-nocheck
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle
} from "react";
import Map, { Source, Layer } from "react-map-gl";
import mapboxgl from "mapbox-gl";
import { useSelector, useDispatch } from "react-redux";
import { setModalStateProps, modalViewsConst } from "../store/modalSlice";
import { setFlyLocation } from "../store/mapStateSlice";
// eslint-disable-next-line import/no-webpack-loader-syntax
import MapboxWorker from "worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker"; //CRAIG CHECK LATER
import { ReactComponent as MapIcon } from "../assets/icons/map-02.svg"
import { ReactComponent as GlobeIcon } from "../assets/icons/globe-02.svg"
import useIsMobile from "../hooks/useIsMobile";
import { buildRectangleFeature } from "../utils/geojsonUtils";

//Layers
import WeatherStationLayer, {
  weatherStationLayerId,
} from "./Layers/WeatherStationLayer";
import AssetLayer, { AssetLayerId } from "./Layers/AssetLayer";
import RainGridLayer from "./Layers/RainGridLayer";

import "mapbox-gl/dist/mapbox-gl.css";
import "react-map-gl-geocoder/dist/mapbox-gl-geocoder.css";
import CircleSelect from "./Layers/CircleSelect";
import PointExploration from "./Layers/PointExploration";
import * as turf from '@turf/turf';

// eslint-disable-next-line import/no-webpack-loader-syntax
// mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

// eslint-disable-next-line import/no-webpack-loader-syntax
// mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;
const mapBoxToken =
  "pk.eyJ1IjoiY3JhaWdtYWMwMDciLCJhIjoiY2xkbHNlZ3h1MDE2bjNxcGJjYWo5YzRtMCJ9.ezesyu3cASMlnZlJLMkZwg";

// How close (in screen pixels) a click must land to the first vertex to seal the polygon
const POLYGON_CLOSE_SNAP_PX = 15;
// Thumbs are less precise than a cursor, so widen the target on touch
const POLYGON_CLOSE_SNAP_PX_MOBILE = 25;
// A rectangle's second corner must land at least this far (screen px) from the first on both axes, or it would be a sliver
const RECTANGLE_MIN_SIZE_PX = 4;

// Stops a group of one dam from slamming the camera to street level when the map opens fitted to the group
const INITIAL_BOUNDS_MAX_ZOOM = 11;

const DRAW_MODES = ["point", "multipoint", "line", "polygon", "rectangle"] as const;
const DRAW_MODE_LABELS: Record<(typeof DRAW_MODES)[number], string> = {
  point: "Point",
  multipoint: "MultiPoint",
  line: "Line",
  polygon: "Polygon",
  rectangle: "Rectangle",
};

const MapViewerReact = forwardRef(({
  defaultLat = 53.51,
  defaultLng = -103.037,
  defaultZoom = 7,
  offset = 0,
  OnClick,
  onGeojsonUpdate = null,
  onDrawModeChange = null,
  contextGeojson = null,
  initialBounds = null,
  onLoad = null,
  fillHeight = false,
  children,
}, ref) => {
  const mapRef = useRef(null);
  const divRef = useRef(null);
  const [cursor, setCursor] = useState<string>("auto");
  const mapState = useSelector((state) => state.mapState);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  
  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    fitBounds: (bounds, options = {}) => {
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        if (map) {
          console.log("Zooming to bounds:", bounds, "with options:", options);
          
          // Add a little delay to ensure the map is fully loaded
          setTimeout(() => {
            map.fitBounds(bounds, {
              ...options,
              linear: false,  // Use fly animation instead of linear
              essential: true // Mark as essential to ensure it runs
            });
          }, 100);
        }
      }
    },
    getMap: () => {
      if (mapRef.current) {
        return mapRef.current.getMap();
      }
      return null;
    }
  }));
  const [bottomDistance, setBottomDistance] = useState(0);
  const [drawMode, setDrawMode] = useState("point"); // point, multipoint, line, polygon, rectangle
  const [geojson, setGeojson] = useState({
    type: "FeatureCollection",
    features: [],
  });
  const [currentFeature, setCurrentFeature] = useState(null);
  // Rectangle mode is two clicks: the first pins a corner here (nothing is in geojson yet), the second completes the Polygon
  const [rectangleAnchor, setRectangleAnchor] = useState(null);
  // Live opposite corner following the cursor, so the rectangle can be previewed before the second click
  const [rectangleHover, setRectangleHover] = useState(null);
  const [centroid, setCentroid] = useState(null);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-streets-v12"); // Default satellite style

  useLayoutEffect(() => {
    const calculateBottomDistance = () => {
      const viewportHeight = window.innerHeight;
      const { top } = divRef.current.getBoundingClientRect();
      setBottomDistance(viewportHeight - top - offset);
    };

    calculateBottomDistance();

    // Optional: Recalculate when the window is resized
    window.addEventListener("resize", calculateBottomDistance);

    // Cleanup event listener
    return () => window.removeEventListener("resize", calculateBottomDistance);
  }, [divRef, offset]);

  // Update the parent component with the current geojson when it changes Prevent infinite update loop by using a ref to track deep changes to geojson
  const geojsonRef = useRef(geojson);
  const centroidRef = useRef(centroid);
  
  useEffect(() => {
    // Only update if the geojson content has meaningfully changed
    if (onGeojsonUpdate && 
        (JSON.stringify(geojsonRef.current) !== JSON.stringify(geojson) ||
         JSON.stringify(centroidRef.current) !== JSON.stringify(centroid))) {
      geojsonRef.current = geojson;
      centroidRef.current = centroid;
      onGeojsonUpdate(geojson, centroid);
    }
  }, [geojson, centroid, onGeojsonUpdate]);
  
  // Initialize from existing geojson data if provided via props
  useEffect(() => {
    // This would be triggered when the component mounts and initialData is available
    if (onGeojsonUpdate && geojson.features.length === 0) {
      // Check if there's initial data available via a callback
      try {
        const initialData = onGeojsonUpdate(null, null, true);
        if (initialData && initialData.features && initialData.features.length > 0) {
          // Set the GeoJSON data from the parent
          setGeojson(initialData);
          
          // Set the current feature
          setCurrentFeature(initialData.features[0]);
          
          // Set draw mode based on the geometry type
          const geometryType = initialData.features[0].geometry.type;
          if (geometryType === "Point") setDrawMode("point");
          else if (geometryType === "MultiPoint") setDrawMode("multipoint");
          else if (geometryType === "LineString") setDrawMode("line");
          else if (geometryType === "Polygon") setDrawMode("polygon");
          
          console.log("Loaded initial GeoJSON data:", geometryType);
          
          // Calculate centroid
          if (geometryType === "Point") {
            setCentroid(initialData.features[0].geometry.coordinates);
          } else {
            try {
              const centroidCoords = turf.centroid(initialData.features[0]).geometry.coordinates;
              setCentroid(centroidCoords);
            } catch (err) {
              console.error("Error calculating centroid:", err);
            }
          }
        }
      } catch (error) {
        console.error("Error loading initial GeoJSON:", error);
      }
    }
  }, []);

  const handleMapClick = (e) => {
    if (mapState.customMarkerActive) {
      handleCustomMarker(e);
      return;
    }

    const { lngLat } = e;
    const { lng, lat } = lngLat;
    const coordinates = [lng, lat];

    // Handle different drawing modes
    switch (drawMode) {
      case "point":
        // Single point mode - create a new point feature
        const pointFeature = {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: coordinates
          },
          properties: {}
        };
        
        setGeojson({
          type: "FeatureCollection",
          features: [pointFeature]
        });
        
        // Calculate centroid (for point, it's the same as the point)
        setCentroid(coordinates);
        
        // Also call the original onClick handler for backward compatibility
        OnClick(e);
        break;
        
      case "multipoint":
        // Add a point to the multipoint collection
        if (!currentFeature || currentFeature.geometry.type !== "MultiPoint") {
          // Create a new multipoint feature if none exists
          const newMultiPoint = {
            type: "Feature",
            geometry: {
              type: "MultiPoint",
              coordinates: [coordinates]
            },
            properties: {}
          };
          setCurrentFeature(newMultiPoint);
          setGeojson({
            type: "FeatureCollection",
            features: [newMultiPoint]
          });
          
          // Initial point becomes the centroid
          setCentroid(coordinates);
        } else {
          // Add a point to existing multipoint
          const updatedCoordinates = [...currentFeature.geometry.coordinates, coordinates];
          const updatedFeature = {
            ...currentFeature,
            geometry: {
              ...currentFeature.geometry,
              coordinates: updatedCoordinates
            }
          };
          setCurrentFeature(updatedFeature);
          setGeojson({
            type: "FeatureCollection",
            features: [updatedFeature]
          });
          
          // Calculate centroid for multipoint
          try {
            const rawCoords = turf.centroid(updatedFeature).geometry.coordinates;
            // Format to 5 decimal places
            const centroidCoords = [
              parseFloat(rawCoords[0].toFixed(5)),
              parseFloat(rawCoords[1].toFixed(5))
            ];
            setCentroid(centroidCoords);
          } catch (e) {
            console.error("Error calculating multipoint centroid:", e);
          }
        }
        break;
        
      case "line":
        // Create or update a LineString
        if (!currentFeature || currentFeature.geometry.type !== "LineString") {
          // Create a new line with the first point
          const newLine = {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: [coordinates]
            },
            properties: {}
          };
          setCurrentFeature(newLine);
          setGeojson({
            type: "FeatureCollection",
            features: [newLine]
          });
          
          // Initial point becomes the centroid for new line
          setCentroid(coordinates);
        } else {
          // Add a point to the existing line
          const updatedCoordinates = [...currentFeature.geometry.coordinates, coordinates];
          const updatedFeature = {
            ...currentFeature,
            geometry: {
              ...currentFeature.geometry,
              coordinates: updatedCoordinates
            }
          };
          setCurrentFeature(updatedFeature);
          setGeojson({
            type: "FeatureCollection",
            features: [updatedFeature]
          });
          
          // Calculate centroid for line
          if (updatedFeature.geometry.coordinates.length >= 2) {
            try {
              const rawCoords = turf.centroid(updatedFeature).geometry.coordinates;
              // Format to 5 decimal places
              const centroidCoords = [
                parseFloat(rawCoords[0].toFixed(5)),
                parseFloat(rawCoords[1].toFixed(5))
              ];
              setCentroid(centroidCoords);
            } catch (e) {
              console.error("Error calculating line centroid:", e);
            }
          }
        }
        break;
        
      case "polygon":
        // Create or update a Polygon
        if (!currentFeature || currentFeature.geometry.type !== "Polygon") {
          // Create a new polygon with the first point
          const newPolygon = {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [[coordinates]] // Note the nested array for polygons
            },
            properties: {}
          };
          setCurrentFeature(newPolygon);
          setGeojson({
            type: "FeatureCollection",
            features: [newPolygon]
          });
          
          // Initial point becomes the centroid for new polygon
          setCentroid(coordinates);
        } else {
          // Add a point to the existing polygon's outer ring Important: create a deep copy of the coordinates to avoid reference issues
          const polygonCoordinates = [...currentFeature.geometry.coordinates[0]];
          const firstPoint = polygonCoordinates[0];
          const lastPoint = polygonCoordinates[polygonCoordinates.length - 1];
          // A sealed ring is never shorter than 4 positions, so [A, A] isn't "closed"
          const ringAlreadyClosed =
            polygonCoordinates.length >= 4 &&
            firstPoint[0] === lastPoint[0] &&
            firstPoint[1] === lastPoint[1];

          // Polygon is complete — ignore further clicks so it can't be corrupted or lost; Reset starts a new one
          if (ringAlreadyClosed) {
            break;
          }

          const map = mapRef.current?.getMap();
          if (!map) break;

          // Snap in screen pixels so the close radius stays zoom-independent
          const firstPointScreen = map.project(firstPoint);
          const pixelDistance = Math.hypot(
            firstPointScreen.x - e.point.x,
            firstPointScreen.y - e.point.y
          );
          const snapPx = isMobile ? POLYGON_CLOSE_SNAP_PX_MOBILE : POLYGON_CLOSE_SNAP_PX;
          const isNearFirstPoint = pixelDistance < snapPx;

          // If clicking close to the first point and we have at least 3 points, close the polygon
          if (isNearFirstPoint && polygonCoordinates.length >= 3) {
            // Add the first point to close the polygon
            const closedCoordinates = [...polygonCoordinates, firstPoint];
            const closedFeature = {
              ...currentFeature,
              geometry: {
                ...currentFeature.geometry,
                coordinates: [closedCoordinates]
              }
            };
            setCurrentFeature(closedFeature);
            setGeojson({
              type: "FeatureCollection",
              features: [closedFeature]
            });
            
            // Calculate centroid for the completed polygon
            try {
              const rawCoords = turf.centroid(closedFeature).geometry.coordinates;
              // Format to 5 decimal places
              const centroidCoords = [
                parseFloat(rawCoords[0].toFixed(5)),
                parseFloat(rawCoords[1].toFixed(5))
              ];
              setCentroid(centroidCoords);
            } catch (e) {
              console.error("Error calculating completed polygon centroid:", e);
            }
          } else {
            // Add the new point to the polygon
            polygonCoordinates.push(coordinates);
            const updatedFeature = {
              ...currentFeature,
              geometry: {
                ...currentFeature.geometry,
                coordinates: [polygonCoordinates]
              }
            };
            setCurrentFeature(updatedFeature);
            setGeojson({
              type: "FeatureCollection",
              features: [updatedFeature]
            });
            
            // Calculate centroid for polygon For polygons with 3+ points, we can calculate a proper centroid
            if (polygonCoordinates.length >= 3) {
              try {
                // Create a temporary closed polygon if needed for centroid calculation
                const tempFeature = JSON.parse(JSON.stringify(updatedFeature));
                const firstPoint = polygonCoordinates[0];
                const lastPoint = polygonCoordinates[polygonCoordinates.length - 1];
                
                // Close the polygon if needed (temporary for centroid calculation)
                if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
                  const closedCoords = [...polygonCoordinates, firstPoint];
                  tempFeature.geometry.coordinates = [closedCoords];
                }
                
                const rawCoords = turf.centroid(tempFeature).geometry.coordinates;
                // Format to 5 decimal places
                const centroidCoords = [
                  parseFloat(rawCoords[0].toFixed(5)),
                  parseFloat(rawCoords[1].toFixed(5))
                ];
                setCentroid(centroidCoords);
              } catch (e) {
                console.error("Error calculating polygon centroid:", e);
              }
            }
          }
        }
        break;
        
      case "rectangle": {
        // Rectangle is complete — ignore further clicks; Reset starts a new one
        if (currentFeature) break;

        if (!rectangleAnchor) {
          setRectangleAnchor(coordinates);
          break;
        }

        const map = mapRef.current?.getMap();
        if (!map) break;

        // Measure in screen pixels so an accidental double-click doesn't produce a zero-size rectangle
        const anchorScreen = map.project(rectangleAnchor);
        if (
          Math.abs(anchorScreen.x - e.point.x) < RECTANGLE_MIN_SIZE_PX ||
          Math.abs(anchorScreen.y - e.point.y) < RECTANGLE_MIN_SIZE_PX
        ) {
          break;
        }

        const rectangleFeature = buildRectangleFeature(rectangleAnchor, coordinates);
        if (!rectangleFeature) break;

        setCurrentFeature(rectangleFeature);
        setGeojson({
          type: "FeatureCollection",
          features: [rectangleFeature]
        });
        setRectangleAnchor(null);
        setRectangleHover(null);

        try {
          const rawCoords = turf.centroid(rectangleFeature).geometry.coordinates;
          // Format to 5 decimal places
          setCentroid([
            parseFloat(rawCoords[0].toFixed(5)),
            parseFloat(rawCoords[1].toFixed(5))
          ]);
        } catch (err) {
          console.error("Error calculating rectangle centroid:", err);
        }
        break;
      }

      default:
        // Default to point behavior
        OnClick(e);
        break;
    }
  };

  const handleMapMouseMove = (e) => {
    if (drawMode !== "rectangle" || !rectangleAnchor) return;
    setRectangleHover([e.lngLat.lng, e.lngLat.lat]);
  };

  const handleCustomMarker = (e) => {
    const { lngLat } = e;
    const { lng, lat } = lngLat;
    const coordinates = { lng, lat };
    
    dispatch(
      setModalStateProps({
        properties: coordinates,
        modalView: modalViewsConst.pointMapForm,
        modalOpen: true,
      })
    );
  };

  const calculateCentroid = () => {
    if (geojson.features.length > 0) {
      const feature = geojson.features[0];
      let centroidCoords;
      
      if (feature.geometry.type === "Point") {
        // For a point, just use its coordinates but ensure 5 decimal places
        const [lng, lat] = feature.geometry.coordinates;
        centroidCoords = [
          parseFloat(lng.toFixed(5)),
          parseFloat(lat.toFixed(5))
        ];
      } else if (feature.geometry.type === "MultiPoint" || 
                 feature.geometry.type === "LineString") {
        // Calculate the centroid and format to 5 decimal places
        const rawCoords = turf.centroid(feature).geometry.coordinates;
        centroidCoords = [
          parseFloat(rawCoords[0].toFixed(5)),
          parseFloat(rawCoords[1].toFixed(5))
        ];
      } else if (feature.geometry.type === "Polygon") {
        // Calculate the centroid and format to 5 decimal places
        const rawCoords = turf.centroid(feature).geometry.coordinates;
        centroidCoords = [
          parseFloat(rawCoords[0].toFixed(5)),
          parseFloat(rawCoords[1].toFixed(5))
        ];
      }
      
      if (centroidCoords) {
        console.log("Centroid:", { lat: centroidCoords[1], lng: centroidCoords[0] });
        // Only update if it's changed or doesn't exist
        if (!centroid || 
            centroid[0] !== centroidCoords[0] || 
            centroid[1] !== centroidCoords[1]) {
          setCentroid(centroidCoords);
        }
        return centroidCoords;
      }
    }
    
    // If no features or centroid couldn't be calculated, clear it
    if (centroid) {
      setCentroid(null);
    }
    return null;
  };

  const testGeojson = () => {
    console.log("GeoJSON:", JSON.stringify(geojson, null, 2));
    calculateCentroid();
  };

  const resetDrawing = () => {
    // Clear all drawing state
    setCurrentFeature(null);
    setRectangleAnchor(null);
    setRectangleHover(null);
    setGeojson({
      type: "FeatureCollection",
      features: []
    });
    setCentroid(null);

    // Notify parent about the reset - this will clear lat/lng in parent component
    if (onGeojsonUpdate) {
      onGeojsonUpdate({
        type: "FeatureCollection",
        features: []
      }, null);
    }
    
    // Clear any lingering layers - more aggressive approach
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      if (map) {
        // Remove any existing drawing layers if they exist
        const layerIds = [
          'point-circle', 'multipoint-circle', 'line-path', 
          'line-vertices', 'polygon-fill', 'polygon-outline', 
          'polygon-vertices', 'centroid-point'
        ];
        
        layerIds.forEach(id => {
          if (map.getLayer(id)) {
            map.removeLayer(id);
          }
        });
        
        // Remove sources
        const sourceIds = [
          'point-source', 'multipoint-source', 'line-source', 
          'line-vertices-source', 'polygon-source', 
          'polygon-vertices-source', 'centroid-source'
        ];
        
        sourceIds.forEach(id => {
          if (map.getSource(id)) {
            map.removeSource(id);
          }
        });
      }
    }
  };
  
  const changeDrawMode = (mode) => {
    // Always reset everything when changing modes, even if it's the same mode
    resetDrawing();
    
    // Then change the mode
    setDrawMode(mode);
    
    // Notify parent component about mode change
    if (onDrawModeChange) {
      onDrawModeChange(mode);
    }
    
    // Force redraw to clear any lingering UI artifacts
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      if (map) {
        map.resize();
      }
    }
    
    // Add a slight delay before allowing new drawings
    setTimeout(() => {
      console.log("Mode changed to:", mode);
    }, 100);
  };


  useEffect(() => {
    if (mapRef.current && mapState.flyLocation) {
      mapRef.current.flyTo({
        center: mapState.flyLocation,
        zoom: 7,
        essential: true,
      });
    }
    return () => {
      dispatch(setFlyLocation(null));
    };
  }, [mapState.flyLocation, mapRef.current]);

  // Use crosshair cursor when in drawing mode
  const onMouseEnter = useCallback(() => {
    // When hovering over interactable elements, use pointer cursor
    setCursor("pointer");
  }, []);
  
  const onMouseLeave = useCallback(() => {
    // When not hovering over interactable elements, use crosshair cursor in drawing mode
    setCursor("crosshair");
  }, []);
  
  // Set initial cursor to crosshair
  useEffect(() => {
    setCursor("crosshair");
  }, []);
  
  return (
    <div
      ref={divRef}
      style={{
        width: "100%",
        position: "relative",
      }}
      className={fillHeight ? "flex flex-col h-full min-h-0" : ""}
    >
      {/* Draw modes are a chip row like every other multi-choice in the app, so they wrap on a phone */}
      <div className={`mb-3 flex flex-wrap items-center gap-2 ${fillHeight ? "shrink-0" : ""}`}>
        {DRAW_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            aria-pressed={drawMode === mode}
            className={`eai-chip ${drawMode === mode ? "eai-chip-active" : ""}`}
            onClick={() => changeDrawMode(mode)}
          >
            {DRAW_MODE_LABELS[mode]}
          </button>
        ))}
        {/* Clearing the drawing is destructive, so it stays red and sits opposite the modes */}
        <button type="button" className="eai-btn-danger-xs ml-auto" onClick={resetDrawing}>
          Reset
        </button>
      </div>

      {/* Add search box overlay */}
      <div
        className={`relative ${fillHeight ? "flex-1 min-h-0" : ""}`}
        style={
          fillHeight
            ? { width: "100%", minHeight: "160px" }
            : { width: "100%", height: `${Math.floor(bottomDistance * 0.67)}px`, minHeight: "300px" }
        }
      >
        <div className="absolute inset-x-0 top-0 z-10">
          {/* Right padding reserves the style toggle's corner so the search box stops short of it */}
          <div className="p-2 pr-[5.25rem]">
            {children && <div>{children}</div>}
          </div>
        </div>
        
        <Map
          ref={mapRef}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={handleMapClick}
          onMouseMove={handleMapMouseMove}
          cursor={cursor}
          mapboxAccessToken={mapBoxToken}
          interactiveLayerIds={[weatherStationLayerId, AssetLayerId]}
          // Fitted bounds (e.g. every asset in a group) take the place of a center and zoom; the map is created already framed, so there is no animation to race with anything else
          initialViewState={
            initialBounds
              ? {
                  bounds: [
                    [initialBounds[0], initialBounds[1]],
                    [initialBounds[2], initialBounds[3]],
                  ],
                  fitBoundsOptions: {
                    padding: isMobile ? 20 : 40,
                    maxZoom: INITIAL_BOUNDS_MAX_ZOOM,
                  },
                }
              : {
                  longitude: defaultLng,
                  latitude: defaultLat,
                  zoom: defaultZoom,
                }
          }
          style={{
            width: "100%",
            height: "100%",
            position: "relative"
          }}
          mapStyle={mapStyle}
          onLoad={(e) => {
            // If we have geojson data on load, zoom to it (unless the caller already framed the map with initialBounds)
            if (!initialBounds && geojson?.features?.length > 0) {
              const feature = geojson.features[0];
              
              // Get bounds of the feature
              const bbox = turf.bbox(feature);
              
              // Check if bounds are valid (have some area)
              const isValidBounds = 
                Math.abs(bbox[0] - bbox[2]) > 0.00001 || 
                Math.abs(bbox[1] - bbox[3]) > 0.00001;
                
              // Use mapbox format for bounds [[west, south], [east, north]]
              const mapboxBounds = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
              
              // Get the current map instance
              const map = mapRef.current.getMap();
              
              if (map && isValidBounds) {
                // Use reduced padding for better visibility on mobile
                const padding = isMobile ? 20 : 40;
                
                // Fit bounds using native Mapbox method
                map.fitBounds(mapboxBounds, {
                  padding: padding,
                  duration: 0,
                  essential: true
                });
              } else if (feature.geometry.type === "Point") {
                // For points, fly to the point with appropriate zoom
                const flyZoom = isMobile ? 5 : 7;
                map.flyTo({
                  center: feature.geometry.coordinates,
                  zoom: flyZoom,
                  duration: 0,
                  essential: true
                });
              }
            }
            
            // Call the onLoad callback from props if provided
            if (onLoad && typeof onLoad === 'function') {
              onLoad(mapRef.current?.getMap());
            }
          }}
        >
        <AssetLayer ref={mapRef} />
        <WeatherStationLayer ref={mapRef} />
        <RainGridLayer ref={mapRef} />
        <CircleSelect ref={mapRef} />
        {/* <PointExploration ref={mapRef} /> */}

        {/* Context layer (e.g., other assets in group): boundaries, routes and points in muted gray, under the geometry being drawn. Each outline has a white casing so it reads on both satellite and light basemaps. */}
        {contextGeojson?.features?.length > 0 && (
          <Source id="context-assets-source" type="geojson" data={contextGeojson}>
            <Layer
              id="context-assets-area-fill"
              type="fill"
              filter={["==", ["get", "kind"], "polygon"]}
              paint={{
                "fill-color": "#9CA3AF", // gray-400
                "fill-opacity": 0.25,
              }}
            />
            <Layer
              id="context-assets-area-casing"
              type="line"
              filter={["==", ["get", "kind"], "polygon"]}
              layout={{ "line-join": "round" }}
              paint={{
                "line-color": "#FFFFFF",
                "line-width": 4,
                "line-opacity": 0.85,
              }}
            />
            <Layer
              id="context-assets-area-outline"
              type="line"
              filter={["==", ["get", "kind"], "polygon"]}
              layout={{ "line-join": "round" }}
              paint={{
                "line-color": "#6B7280", // gray-500
                "line-width": 2,
              }}
            />
            <Layer
              id="context-assets-line-casing"
              type="line"
              filter={["==", ["get", "kind"], "line"]}
              layout={{ "line-cap": "round", "line-join": "round" }}
              paint={{
                "line-color": "#FFFFFF",
                "line-width": 6,
                "line-opacity": 0.85,
              }}
            />
            <Layer
              id="context-assets-line"
              type="line"
              filter={["==", ["get", "kind"], "line"]}
              layout={{ "line-cap": "round", "line-join": "round" }}
              paint={{
                "line-color": "#6B7280", // gray-500
                "line-width": 3,
              }}
            />
            <Layer
              id="context-assets-points"
              type="circle"
              filter={["==", ["get", "kind"], "point"]}
              paint={{
                "circle-radius": 5,
                "circle-color": "#9CA3AF", // gray-400
                "circle-opacity": 0.7,
                "circle-stroke-width": 1,
                "circle-stroke-color": "#FFFFFF",
              }}
            />
          </Source>
        )}
        
        {/* Map style — two exclusive views, so the DS segmented control, floated over the map. The track keeps its 40px height, which leaves each icon item a square 32px. */}
        <div className="map-controls absolute right-2 top-2 z-10">
          <div className="eai-seg bg-eai-surface-card shadow-eai-card">
            {[
              { style: "mapbox://styles/mapbox/light-v9", Icon: MapIcon, title: "Map View" },
              { style: "mapbox://styles/mapbox/satellite-streets-v12", Icon: GlobeIcon, title: "Satellite View" },
            ].map(({ style, Icon, title }) => (
              <button
                key={style}
                type="button"
                aria-pressed={mapStyle === style}
                className={`eai-seg-item w-eai-control-sm px-0 ${mapStyle === style ? "eai-seg-item-active" : ""}`}
                onClick={() => setMapStyle(style)}
                title={title}
              >
                {/* The icon hardcodes stroke="black", so currentColor has to be forced in */}
                <Icon className="h-4 w-4 [&_path]:stroke-current" />
              </button>
            ))}
          </div>
        </div>
        
        {/* Centroid visualization */}
        {centroid && (
          <Source id="centroid-source" type="geojson" data={{
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: centroid
            },
            properties: {}
          }}>
            <Layer
              id="centroid-point"
              type="circle"
              paint={{
                'circle-radius': 7,
                'circle-color': '#A2BBBF',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff'
              }}
            />
          </Source>
        )}
        
        {/* Rectangle in progress: the pinned first corner, plus a dashed preview that follows the cursor */}
        {drawMode === "rectangle" && rectangleAnchor && (
          <>
            {(() => {
              const previewFeature = rectangleHover
                ? buildRectangleFeature(rectangleAnchor, rectangleHover)
                : null;
              return previewFeature ? (
                <Source id="rectangle-preview-source" type="geojson" data={previewFeature}>
                  <Layer
                    id="rectangle-preview-fill"
                    type="fill"
                    paint={{
                      'fill-color': '#FFCC00',
                      'fill-opacity': 0.15
                    }}
                  />
                  <Layer
                    id="rectangle-preview-outline"
                    type="line"
                    paint={{
                      'line-color': '#FFCC00',
                      'line-width': 2,
                      'line-dasharray': [2, 2]
                    }}
                  />
                </Source>
              ) : null;
            })()}
            <Source id="rectangle-anchor-source" type="geojson" data={{
              type: "Feature",
              geometry: { type: "Point", coordinates: rectangleAnchor },
              properties: {}
            }}>
              <Layer
                id="rectangle-anchor-point"
                type="circle"
                paint={{
                  'circle-radius': 4,
                  'circle-color': '#FFCC00'
                }}
              />
            </Source>
          </>
        )}

        {/* GeoJSON drawing layers - conditionally render based on draw mode */}
        {geojson.features.length > 0 && (
          <>
            {/* Source and layer for Point features - ONLY SHOW IN POINT MODE */}
            {drawMode === "point" && geojson.features.some(f => f.geometry.type === "Point") && (
              <Source id="point-source" type="geojson" data={(() => {
                return {
                  type: "FeatureCollection",
                  features: geojson.features.filter(f => f.geometry.type === "Point")
                };
              })()}>
                <Layer
                  id="point-circle"
                  type="circle"
                  paint={{
                    'circle-radius': 5,
                    'circle-color': '#FFCC00'
                  }}
                />
              </Source>
            )}
            
            {/* Source and layer for MultiPoint features - ONLY SHOW IN MULTIPOINT MODE */}
            {drawMode === "multipoint" && geojson.features.some(f => f.geometry.type === "MultiPoint") && (
              <Source id="multipoint-source" type="geojson" data={(() => {
                // Create this data once per render to avoid infinite re-renders
                return {
                  type: "FeatureCollection",
                  features: geojson.features
                    .filter(f => f.geometry.type === "MultiPoint")
                    .flatMap(f => f.geometry.coordinates.map(coord => ({
                      type: "Feature",
                      geometry: {
                        type: "Point",
                        coordinates: coord
                      },
                      properties: {}
                    })))
                };
              })()}>
                <Layer
                  id="multipoint-circle"
                  type="circle"
                  paint={{
                    'circle-radius': 5,
                    'circle-color': '#FFCC00'
                  }}
                />
              </Source>
            )}
            
            {/* Source and layer for LineString features - ONLY SHOW IN LINE MODE */}
            {drawMode === "line" && geojson.features.some(f => f.geometry.type === "LineString") && (
              <Source id="line-source" type="geojson" data={(() => {
                return {
                  type: "FeatureCollection",
                  features: geojson.features.filter(f => f.geometry.type === "LineString")
                };
              })()}>
                <Layer
                  id="line-path"
                  type="line"
                  paint={{
                    'line-color': '#FFCC00',
                    'line-width': 2
                  }}
                />
              </Source>
            )}
            
            {/* Source and layer for vertices of LineString features - ONLY SHOW IN LINE MODE */}
            {drawMode === "line" && geojson.features.some(f => f.geometry.type === "LineString") && (
              <Source id="line-vertices-source" type="geojson" data={(() => {
                return {
                  type: "FeatureCollection",
                  features: geojson.features
                    .filter(f => f.geometry.type === "LineString")
                    .flatMap(f => f.geometry.coordinates.map(coord => ({
                      type: "Feature",
                      geometry: {
                        type: "Point",
                        coordinates: coord
                      },
                      properties: {}
                    })))
                };
              })()}>
                <Layer
                  id="line-vertices"
                  type="circle"
                  paint={{
                    'circle-radius': 4,
                    'circle-color': '#FFCC00'
                  }}
                />
              </Source>
            )}
            
            {/* Source and layer for Polygon features - ONLY SHOW IN POLYGON / RECTANGLE MODE (a rectangle is a Polygon) */}
            {(drawMode === "polygon" || drawMode === "rectangle") && geojson.features.some(f => f.geometry.type === "Polygon") && (
              <Source id="polygon-source" type="geojson" data={(() => {
                return {
                  type: "FeatureCollection",
                  features: geojson.features.filter(f => f.geometry.type === "Polygon")
                };
              })()}>
                <Layer
                  id="polygon-fill"
                  type="fill"
                  paint={{
                    'fill-color': '#FFCC00',
                    'fill-opacity': 0.3
                  }}
                />
                
                <Layer
                  id="polygon-outline"
                  type="line"
                  paint={{
                    'line-color': '#FFCC00',
                    'line-width': 2
                  }}
                />
              </Source>
            )}
            
            {/* Source and layer for vertices of Polygon features - ONLY SHOW IN POLYGON / RECTANGLE MODE */}
            {(drawMode === "polygon" || drawMode === "rectangle") && geojson.features.some(f => f.geometry.type === "Polygon") && (
              <Source id="polygon-vertices-source" type="geojson" data={(() => {
                return {
                  type: "FeatureCollection",
                  features: geojson.features
                    .filter(f => f.geometry.type === "Polygon")
                    .flatMap(f => f.geometry.coordinates[0].map(coord => ({
                      type: "Feature",
                      geometry: {
                        type: "Point",
                        coordinates: coord
                      },
                      properties: {}
                    })))
                };
              })()}>
                <Layer
                  id="polygon-vertices"
                  type="circle"
                  paint={{
                    'circle-radius': 4,
                    'circle-color': '#FFCC00'
                  }}
                />
              </Source>
            )}
          </>
        )}
      </Map>
      </div>

      {/* Small text showing centroid coordinates */}
      {centroid ? (
        <div className={`mt-1.5 px-2 font-inter text-eai-body-xs text-eai-ink-subtle ${fillHeight ? "shrink-0" : ""}`}>
          Centroid located at Latitude: {centroid[1].toFixed(4)}, Longitude: {centroid[0].toFixed(4)}
        </div>
      ) : (
        // Reserves the centroid line's height so the map doesn't jump when one appears
        <div aria-hidden className={`invisible mt-1.5 px-2 font-inter text-eai-body-xs ${fillHeight ? "shrink-0" : ""}`}>
          {"x"}
        </div>
      )}
    </div>
  );
});

export default MapViewerReact;
